import { useEffect, useRef, useState, useCallback } from "react";
import { Loader2, X, Ban } from "lucide-react";
import { pipelinesApi } from "@/api/mist";
import { useSession } from "@/context/SessionContext";
import { toast } from "sonner";

/* Global "Rendering…" dock. Polls the caller's active pipeline jobs every few
   seconds and, whenever one or more renders are in flight, shows a floating pill
   (bottom-centre, above the nav) that lists them and lets the user Cancel any
   single render or Cancel all. A cancel kills the live ffmpeg encode server-side
   and refunds any held coins. Mounted once in the app chassis, so it works
   site-wide across all 16 studios without touching each studio's own polling. */
export default function RenderDock() {
  const { user } = useSession();
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState({});     // job_id -> cancelling
  const [allBusy, setAllBusy] = useState(false);
  const timer = useRef(null);

  const poll = useCallback(async () => {
    try {
      const r = await pipelinesApi.activeJobs();
      setJobs(Array.isArray(r?.jobs) ? r.jobs : []);
    } catch {
      /* transient — keep the last known list, never crash the dock */
    }
  }, []);

  useEffect(() => {
    if (!user) { setJobs([]); return; }
    poll();
    timer.current = setInterval(poll, 4000);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [user, poll]);

  const cancelOne = async (jobId) => {
    setBusy((b) => ({ ...b, [jobId]: true }));
    try {
      const res = await pipelinesApi.cancelJob(jobId);
      setJobs((js) => js.filter((j) => j.job_id !== jobId));
      toast(res?.refunded ? "Render cancelled — coins refunded" : "Render cancelled", { icon: "🛑" });
      window.dispatchEvent(new Event("mist:coins-updated"));
      poll();
    } catch {
      toast("Couldn't cancel that render — try again");
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[jobId]; return n; });
    }
  };

  const cancelAll = async () => {
    setAllBusy(true);
    try {
      const res = await pipelinesApi.cancelAllJobs();
      setJobs([]);
      toast(`Cancelled ${res?.cancelled || 0} render${(res?.cancelled || 0) === 1 ? "" : "s"}`, { icon: "🛑" });
      window.dispatchEvent(new Event("mist:coins-updated"));
      poll();
    } catch {
      toast("Couldn't cancel — try again");
    } finally {
      setAllBusy(false);
    }
  };

  if (!user || jobs.length === 0) return null;

  return (
    <div
      data-testid="render-dock"
      className="absolute left-1/2 -translate-x-1/2 bottom-24 z-[70] w-[92%] max-w-[380px]"
    >
      <div className="rounded-2xl border-2 border-black bg-[#0B1220]/95 backdrop-blur-md shadow-[4px_4px_0_0_#000] overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
          <div className="flex items-center gap-2 text-[#00F5D4]">
            <Loader2 size={15} className="animate-spin" />
            <span className="text-xs font-semibold tracking-wide" data-testid="render-dock-count">
              Rendering · {jobs.length}
            </span>
          </div>
          {jobs.length > 1 && (
            <button
              type="button"
              onClick={cancelAll}
              disabled={allBusy}
              data-testid="render-dock-cancel-all"
              className="flex items-center gap-1 text-[11px] font-semibold text-red-300 hover:text-red-200 disabled:opacity-50 transition-colors"
            >
              {allBusy ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} />}
              Cancel all
            </button>
          )}
        </div>
        <ul className="max-h-48 overflow-y-auto divide-y divide-white/5">
          {jobs.map((j) => {
            const pct = typeof j.progress === "number" ? j.progress : null;
            return (
              <li
                key={j.job_id}
                data-testid={`render-dock-job-${j.job_id}`}
                className="flex items-center gap-2 px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-medium text-white truncate">{j.label || "Render"}</div>
                  <div className="text-[10px] text-white/50 truncate">
                    {j.stage || (pct != null ? `${pct}%` : "Working…")}
                  </div>
                  <div className="mt-1 h-1 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#00F5D4] to-[#9EFFB8] transition-[width] duration-500"
                      style={{ width: `${pct != null ? Math.max(4, pct) : 30}%` }}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => cancelOne(j.job_id)}
                  disabled={!!busy[j.job_id]}
                  data-testid={`render-dock-cancel-${j.job_id}`}
                  aria-label="Cancel this render"
                  className="shrink-0 grid place-items-center h-7 w-7 rounded-full bg-red-500/15 text-red-300 hover:bg-red-500/30 disabled:opacity-50 transition-colors"
                >
                  {busy[j.job_id] ? <Loader2 size={13} className="animate-spin" /> : <X size={14} />}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
